import { ErrorHandler, Injectable, inject } from '@angular/core';
import { injectRumService } from '../services/rum/rum.service';
import { WindowReloadService } from '../services/window-reload/window-reload.service';

// Only attempt a reload once per window
const RELOAD_GUARD_KEY = 'dpqa-chunk-reload-last-attempt';
const RELOAD_GUARD_WINDOW_MS = 10_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Loading chunk [\w]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
  private rumService = injectRumService();
  private windowReloadService = inject(WindowReloadService);

  override handleError(error: unknown): void {
    if (!this.isChunkLoadError(error)) {
      super.handleError(error);
      return;
    }

    this.rumService.awsRum()?.recordEvent('dpqa.chunk-load-error', {
      message: this.extractErrorMessage(error),
    });

    this.reloadOnce();
  }

  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string') {
      return (error as { message: string }).message;
    }

    return '';
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.extractErrorMessage(error);
    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
  }

  private reloadOnce(): void {
    const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    const now = Date.now();

    if (now - lastAttempt < RELOAD_GUARD_WINDOW_MS) {
      return;
    }

    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    this.windowReloadService.reload();
  }
}
